import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { MapContainer, TileLayer, GeoJSON, Circle, CircleMarker, Popup, ZoomControl, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { api } from "./api";
import { FormularioReporte, ConsultaReporte } from "./Reportar";
import Ayuda from "./Ayuda";

const CENTRO = [-38.0, -71.0]; // Chile: el mapa abre vacío y se acerca al elegir una zona
const ZOOM_INICIAL = 4;
const CORREO = "checklist@harrys.cl";
const MAILTO = `mailto:${CORREO}?subject=${encodeURIComponent("Consulta sobre Mejoremos el Barrio")}`;
const LIMITE_OBJETOS = 5000;
const VACIO = { rows: [], total: 0, truncado: false };
const PALETA = ["#1f77b4", "#d62728", "#2ca02c", "#ff7f0e", "#9467bd", "#8c564b", "#e377c2", "#17becf", "#bcbd22", "#7f7f7f"];

// Color estable por tipo (el del Tipo, o uno de la paleta según su código): no cambia al elegir otras zonas.
function colorTipo(t) {
    if (t && t.xcolor) return t.xcolor;
    const clave = (t && (t.codigo || t.nombre)) || "";
    let h = 0;
    for (let i = 0; i < clave.length; i++) h = (h * 31 + clave.charCodeAt(i)) >>> 0;
    return PALETA[h % PALETA.length];
}
// Estados generales (xobj.estado_gral): orden de presentación y color. '' = objeto sin estado.
const ESTADO_ORDEN = ["MUY_BUENO", "BUENO", "REGULAR", "MALO", "QUEBRADO", "FUERA_SERVICIO", "SIN_EVALUAR", ""];
const ESTADO_COLOR = { MUY_BUENO: "#1b8a3a", BUENO: "#7cb342", REGULAR: "#f9a825", MALO: "#ef6c00", QUEBRADO: "#c62828", FUERA_SERVICIO: "#616161", SIN_EVALUAR: "#90a4ae", "": "#cfd8dc" };
const ESTADO_NOMBRE = { MUY_BUENO: "Muy bueno", BUENO: "Bueno", REGULAR: "Regular", MALO: "Malo", QUEBRADO: "Quebrado", FUERA_SERVICIO: "Fuera de servicio", SIN_EVALUAR: "Sin evaluar", "": "Sin estado" };
const colorEstado = (codigo) => ESTADO_COLOR[codigo || ""] || "#90a4ae";
const nombreEstado = (e) => (e && e.descripcion) || (e && e.codigo ? e.codigo.replace(/_/g, " ").toLowerCase() : "Sin estado");
// Filtros numéricos: campo del objeto -> título y unidad
const FILTROS_NUM = [
    { campo: "ano_fab", titulo: "Año de fabricación" },
    { campo: "ano_inst", titulo: "Año de instalación" },
    { campo: "meses_vid_util", titulo: "Vida útil (meses)" },
];
// Fin de vida útil: meses restantes = vida útil (meses) - 12 x (año actual - año base). Los atajos fijan [desde, hasta].
const PRESETS_VIDA = [
    { id: "todos", texto: "Todos", rango: ["", ""] },
    { id: "vencidos", texto: "Ya vencidos", rango: ["", "0"] },
    { id: "1a", texto: "Vencen en ≤ 1 año", rango: ["1", "12"] },
    { id: "3a", texto: "Vencen en ≤ 3 años", rango: ["1", "36"] },
    { id: "5a", texto: "Vencen en ≤ 5 años", rango: ["1", "60"] },
    { id: "vigentes", texto: "Vigentes", rango: ["1", ""] },
];
const textoAnios = (meses) => { const a = Math.abs(meses) / 12; return `${Math.abs(meses)} meses (${a.toFixed(1).replace(/\.0$/, "")} años)`; };
const textoRestante = (m) => (m > 0 ? `Vida útil restante: ${textoAnios(m)}` : m === 0 ? "Cumple su vida útil este año" : `Vida útil vencida hace ${textoAnios(m)}`);
const FILTROS_VACIOS = { ano_fab: ["", ""], ano_inst: ["", ""], meses_vid_util: ["", ""] };
const aNumero = (t) => (t === "" || t == null || !Number.isFinite(Number(t)) ? null : Math.round(Number(t)));
const sinTildes = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
const PROBLEMA_RESUELTO = { FALLA: "No funcionaba", DANO: "Estaba dañado", PELIGRO: "Presentaba un peligro", VANDALIZADO: "Estaba vandalizado", OTRO: "Tenía un problema" };
const fmt = (n) => Number(n).toLocaleString("es-CL");

// El enlace guarda las zonas elegidas (#r=id,id) para poder compartir la vista.
function zonasDelEnlace() {
    const m = window.location.hash.match(/[#&]r=([0-9a-f,-]+)/i);
    return m ? m[1].split(",").filter(Boolean) : [];
}

// MapContainer solo usa center/zoom al montar: se reencuadra cuando cambia `fit.token`.
function Encuadrar({ fit }) {
    const map = useMap();
    useEffect(() => {
        if (!fit.bounds) return;
        const b = L.latLngBounds(fit.bounds);
        if (b.isValid()) map.fitBounds(b, { padding: [40, 40], maxZoom: 16 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fit.token]);
    return null;
}

// Informa el rectángulo visible y el zoom (al montar y cada vez que se mueve el mapa).
function VistaMapa({ onVista }) {
    const map = useMap();
    const informar = useCallback(() => {
        const b = map.getBounds().pad(0.1);
        onVista({ bbox: [b.getSouth(), b.getWest(), b.getNorth(), b.getEast()].map((n) => Number(n.toFixed(6))), zoom: map.getZoom() });
    }, [map, onVista]);
    useEffect(() => { informar(); }, [informar]);
    useMapEvents({ moveend: informar, zoomend: informar });
    return null;
}

// Mientras se marca un lugar para reportar, el clic en el mapa fija el punto
function ClickMapa({ activo, onPunto }) {
    useMapEvents({ click: (e) => { if (activo) onPunto(e.latlng); } });
    return null;
}

export default function App() {
    const [raices, setRaices] = useState(null); // null = cargando
    const [errorRaices, setErrorRaices] = useState(null);
    const [sel, setSel] = useState([]); // zonas (objetos padre) elegidas
    const [tipos, setTipos] = useState([]); // tipos que existen dentro de las zonas elegidas
    const [cargandoTipos, setCargandoTipos] = useState(false);
    const [tiposOff, setTiposOff] = useState(() => new Set()); // tipos desmarcados por el usuario
    const [estados, setEstados] = useState([]); // estados que existen dentro de la selección (y tipos marcados)
    const [cargandoEstados, setCargandoEstados] = useState(false);
    const [estadosOff, setEstadosOff] = useState(() => new Set()); // estados desmarcados por el usuario
    const [rangosDatos, setRangosDatos] = useState({}); // min/max reales de cada campo dentro de la selección
    const [filtros, setFiltros] = useState(FILTROS_VACIOS); // lo que escribe el usuario: campo -> [desde, hasta]
    const [vidaBase, setVidaBase] = useState("inst"); // el fin de vida se cuenta desde la instalación o la fabricación
    const [vidaRango, setVidaRango] = useState(["", ""]); // meses restantes [desde, hasta] (negativo = ya vencido)
    const [areas, setAreas] = useState({}); // id zona -> GeoJSON
    const [objetos, setObjetos] = useState(VACIO);
    const [cargandoObj, setCargandoObj] = useState(false);
    const [vista, setVista] = useState(null);
    const [panel, setPanel] = useState(false);
    const [busca, setBusca] = useState("");
    const [fit, setFit] = useState({ token: 0, bounds: null });
    const [punto, setPunto] = useState(null);
    const [aviso, setAviso] = useState(null);
    const [reporte, setReporte] = useState(null);       // formulario abierto: { objeto } o { lugar: {lat, lon} }
    const [ubicando, setUbicando] = useState(false);    // esperando que se marque en el mapa un lugar para reportar
    const [instalar, setInstalar] = useState(null);      // evento "beforeinstallprompt" (Chrome/Android): permite ofrecer "Instalar"
    const [miPos, setMiPos] = useState(null);           // posición actual del dispositivo: { lat, lon, precision (m) }
    const [buscandoPos, setBuscandoPos] = useState(false);
    const [ayuda, setAyuda] = useState(false);          // guía "¿Cómo funciona?"
    const [consulta, setConsulta] = useState(false);    // ventana "consultar un reporte"
    const [verResueltos, setVerResueltos] = useState(false); // capa de reportes resueltos (publicados por quien administra)
    const [resueltos, setResueltos] = useState({ rows: [], total: 0 });
    const [resuelto, setResuelto] = useState(null);     // reporte resuelto con el globo abierto
    const pendienteFit = useRef(false);
    const zonasIniciales = useRef(zonasDelEnlace()); // se lee antes de que el efecto de abajo reescriba el enlace

    // ---- zonas (objetos sin padre) ------------------------------------------------------
    useEffect(() => {
        const ac = new AbortController();
        api.raices(ac.signal)
            .then((o) => {
                const lista = o.raices || [];
                setRaices(lista);
                const ids = zonasIniciales.current.filter((id) => lista.some((r) => r.id_objeto === id));
                if (ids.length) { pendienteFit.current = true; setSel(ids); }
            })
            .catch((e) => { if (e.name !== "AbortError") setErrorRaices(e.message); });
        return () => ac.abort();
    }, []);

    // ---- al cambiar la selección: enlace + tipos que existen dentro de las zonas ---------
    useEffect(() => {
        const hash = sel.length ? `#r=${sel.join(",")}` : "";
        window.history.replaceState(null, "", window.location.pathname + window.location.search + hash);
        if (!sel.length) { setTipos([]); setCargandoTipos(false); return; }
        const ac = new AbortController();
        setCargandoTipos(true);
        api.tipos(sel, ac.signal)
            .then((o) => { setTipos(o.tipos || []); setCargandoTipos(false); })
            .catch((e) => { if (e.name !== "AbortError") { setAviso(e.message); setCargandoTipos(false); } });
        return () => ac.abort();
    }, [sel]);

    // ---- área de las zonas elegidas; al terminar, el mapa se acerca a ellas -------------
    useEffect(() => {
        if (!raices || !sel.length) return;
        const faltan = sel.filter((id) => {
            const r = raices.find((x) => x.id_objeto === id);
            return r && r.tiene_geom && !(id in areas);
        });
        if (!faltan.length) {
            if (pendienteFit.current) {
                pendienteFit.current = false;
                const b = L.latLngBounds([]);
                sel.forEach((id) => {
                    const r = raices.find((x) => x.id_objeto === id);
                    if (areas[id]) b.extend(L.geoJSON(areas[id]).getBounds());
                    else if (r && Number(r.gpslat) && Number(r.gpslon)) b.extend([Number(r.gpslat), Number(r.gpslon)]);
                });
                if (b.isValid()) setFit((f) => ({ token: f.token + 1, bounds: [[b.getSouth(), b.getWest()], [b.getNorth(), b.getEast()]] }));
            }
            return;
        }
        const ac = new AbortController();
        api.areas(faltan, ac.signal)
            .then((o) => {
                const nuevos = {};
                faltan.forEach((id) => { nuevos[id] = null; }); // sin área: no se reintenta
                (o.areas || []).forEach((a) => { nuevos[a.id_objeto] = a.geojson; });
                setAreas((prev) => ({ ...prev, ...nuevos }));
            })
            .catch((e) => { if (e.name !== "AbortError") setAviso(e.message); });
        return () => ac.abort();
    }, [sel, raices, areas]);

    // ---- objetos: zonas + tipos marcados + zona visible del mapa ------------------------
    const tiposActivos = useMemo(() => tipos.filter((t) => !tiposOff.has(t.id_tipo)).map((t) => t.id_tipo), [tipos, tiposOff]);
    // estados que hay dentro de la selección, contando solo los tipos marcados
    useEffect(() => {
        if (!sel.length || !tiposActivos.length) { setEstados([]); setCargandoEstados(false); return; }
        const ac = new AbortController();
        setCargandoEstados(true);
        api.estados(sel, tiposActivos, ac.signal)
            .then((o) => { setEstados(o.estados || []); setCargandoEstados(false); })
            .catch((e) => { if (e.name !== "AbortError") { setAviso(e.message); setCargandoEstados(false); } });
        return () => ac.abort();
    }, [sel, tiposActivos]);

    // Solo se envía el filtro si el usuario desmarcó algún estado (y la lista ya cargó); '' = sin estado
    const estadosActivos = useMemo(
        () => (estadosOff.size && estados.length ? estados.filter((e) => !estadosOff.has(e.codigo)).map((e) => e.codigo) : null),
        [estados, estadosOff]);

    // mínimo y máximo reales de año/vida útil dentro de la selección (se muestran como guía en los filtros)
    useEffect(() => {
        if (!sel.length || !tiposActivos.length) { setRangosDatos({}); return; }
        const ac = new AbortController();
        api.rangos(sel, tiposActivos, vidaBase, ac.signal)
            .then((o) => setRangosDatos(o.rangos || {}))
            .catch((e) => { if (e.name !== "AbortError") setAviso(e.message); });
        return () => ac.abort();
    }, [sel, tiposActivos, vidaBase]);

    // Solo se envían los campos con algún límite escrito; los objetos sin dato en ese campo quedan fuera
    const rangosActivos = useMemo(() => {
        const out = {};
        FILTROS_NUM.forEach(({ campo }) => {
            const [d, h] = filtros[campo].map(aNumero);
            if (d !== null || h !== null) out[campo] = [d, h];
        });
        return Object.keys(out).length ? out : null;
    }, [filtros]);

    // La base se envía siempre (así cada objeto trae sus meses restantes); min/max solo si se escribieron
    const vidaFiltroActivo = vidaRango.some((v) => aNumero(v) !== null);
    const vida = useMemo(() => ({ base: vidaBase, min: aNumero(vidaRango[0]), max: aNumero(vidaRango[1]) }), [vidaBase, vidaRango]);

    useEffect(() => {
        if (!sel.length || !tiposActivos.length || !vista) { setObjetos(VACIO); setCargandoObj(false); return; }
        const ac = new AbortController();
        setCargandoObj(true);
        const espera = setTimeout(() => {
            api.objetos({ raices: sel, tipos: tiposActivos, bbox: vista.bbox, limite: LIMITE_OBJETOS, estados: estadosActivos, rangos: rangosActivos, vida }, ac.signal)
                .then((o) => { setObjetos({ rows: o.rows || [], total: o.total || 0, truncado: !!o.truncado }); setCargandoObj(false); })
                .catch((e) => { if (e.name !== "AbortError") { setAviso(e.message); setCargandoObj(false); } });
        }, 300);
        return () => { clearTimeout(espera); ac.abort(); };
    }, [sel, tiposActivos, estadosActivos, rangosActivos, vida, vista]);

    // Reportes resueltos de las zonas elegidas (últimos 12 meses); solo si se activa la capa
    useEffect(() => {
        if (!verResueltos || !sel.length) { setResueltos({ rows: [], total: 0 }); setResuelto(null); return; }
        const ac = new AbortController();
        api.resueltos(sel, ac.signal)
            .then((o) => setResueltos({ rows: o.rows || [], total: o.total || 0 }))
            .catch((e) => { if (e.name !== "AbortError") setAviso(e.message); });
        return () => ac.abort();
    }, [verResueltos, sel]);

    useEffect(() => {
        if (window.__instalarEvt) setInstalar(window.__instalarEvt);   // el aviso llegó antes de que React cargara
        const guardar = (e) => { e.preventDefault(); window.__instalarEvt = e; setInstalar(e); };
        const instalada = () => setInstalar(null);
        window.addEventListener("beforeinstallprompt", guardar);
        window.addEventListener("appinstalled", instalada);
        return () => { window.removeEventListener("beforeinstallprompt", guardar); window.removeEventListener("appinstalled", instalada); };
    }, []);
    const yaInstalada = typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);
    // Siempre se ofrece si no está instalada: con el aviso del navegador instala directo; si no, abre la guía con los pasos de ese navegador.
    const puedeInstalar = !yaInstalada;
    const pedirInstalar = () => {
        if (!instalar) return;
        instalar.prompt();
        instalar.userChoice.finally(() => { window.__instalarEvt = null; setInstalar(null); });
    };

    // Escape cierra el panel
    useEffect(() => {
        const alTeclear = (e) => { if (e.key === "Escape") { setPanel(false); setUbicando(false); setAyuda(false); } };
        window.addEventListener("keydown", alTeclear);
        return () => window.removeEventListener("keydown", alTeclear);
    }, []);

    // ---- acciones -------------------------------------------------------------------------
    const alternarZona = (id) => {
        setPunto(null);
        if (!sel.includes(id)) pendienteFit.current = true; // al agregar una zona, el mapa se acerca a ella
        setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
    };
    const elegirLugar = (latlng) => { setUbicando(false); setReporte({ lugar: { lat: latlng.lat, lon: latlng.lng } }); };
    // Botón "mi ubicación": centra el mapa en donde está la persona y la marca con un punto azul
    const irAMiUbicacion = () => {
        if (!navigator.geolocation) { setAviso("Tu navegador no permite obtener la ubicación."); return; }
        setBuscandoPos(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude: lat, longitude: lon, accuracy } = pos.coords;
                setMiPos({ lat, lon, precision: accuracy });
                const d = Math.min(Math.max((accuracy || 50) / 111000, 0.002), 0.02);   // acerca según la precisión (≈ grados)
                setFit((f) => ({ token: f.token + 1, bounds: [[lat - d, lon - d], [lat + d, lon + d]] }));
                setBuscandoPos(false);
            },
            (err) => {
                setBuscandoPos(false);
                setAviso(err && err.code === 1
                    ? "No tenemos permiso para ver tu ubicación. Actívala para este sitio en el navegador e inténtalo de nuevo."
                    : "No pudimos obtener tu ubicación. Revisa que el GPS esté activo e inténtalo de nuevo.");
            },
            { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 });
    };
    const usarMiUbicacion = () => {
        if (!navigator.geolocation) { setAviso("Tu navegador no permite obtener la ubicación. Toca el mapa para marcar el lugar."); return; }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude: lat, longitude: lon } = pos.coords;
                setFit((f) => ({ token: f.token + 1, bounds: [[lat - 0.002, lon - 0.002], [lat + 0.002, lon + 0.002]] }));
                setUbicando(false); setReporte({ lugar: { lat, lon } });
            },
            () => setAviso("No pudimos obtener tu ubicación. Toca el mapa para marcar el lugar."),
            { enableHighAccuracy: true, timeout: 10000 });
    };
    const limpiarZonas = () => { setPunto(null); setSel([]); };
    const alternarTipo = (id) => setTiposOff((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const alternarEstado = (codigo) => setEstadosOff((prev) => { const n = new Set(prev); if (n.has(codigo)) n.delete(codigo); else n.add(codigo); return n; });
    const cambiarFiltro = (campo, i, valor) => setFiltros((prev) => ({ ...prev, [campo]: prev[campo].map((v, k) => (k === i ? valor : v)) }));
    const limpiarFiltros = () => setFiltros(FILTROS_VACIOS);
    const cambiarVida = (i, valor) => setVidaRango((prev) => prev.map((v, k) => (k === i ? valor : v)));
    const presetVidaActivo = (p) => p.rango[0] === vidaRango[0] && p.rango[1] === vidaRango[1];
    const todosEstados = (marcar) => setEstadosOff(marcar ? new Set() : new Set(estados.map((e) => e.codigo)));
    const todosTipos = (marcar) => setTiposOff(marcar ? new Set() : new Set(tipos.map((t) => t.id_tipo)));

    const estadoPorCodigo = useMemo(() => Object.fromEntries(estados.map((e) => [e.codigo, e])), [estados]);
    const estadosOrdenados = useMemo(() => [...estados].sort((a, b) => {
        const ia = ESTADO_ORDEN.indexOf(a.codigo), ib = ESTADO_ORDEN.indexOf(b.codigo);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.codigo.localeCompare(b.codigo);
    }), [estados]);
    const tipoPorId = useMemo(() => Object.fromEntries(tipos.map((t) => [t.id_tipo, t])), [tipos]);
    const tituloZonas = useMemo(() => {
        const nombres = [...new Set((raices || []).map((r) => r.tipo_nombre).filter(Boolean))];
        return nombres.length === 1 ? nombres[0] : "Zona";
    }, [raices]);
    const zonasFiltradas = useMemo(() => {
        const q = sinTildes(busca).trim();
        return (raices || []).filter((r) => !q || sinTildes(r.nombre || r.codigo).includes(q));
    }, [raices, busca]);
    const zonasElegidas = (raices || []).filter((r) => sel.includes(r.id_objeto));
    const hayTipos = tipos.length > 0;

    return (
        <div className={"app" + (ubicando ? " ubicando" : "")}>
            <MapContainer center={CENTRO} zoom={ZOOM_INICIAL} zoomControl={false} preferCanvas className="mapa">
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <ZoomControl position="bottomright" />
                <Encuadrar fit={fit} />
                <VistaMapa onVista={setVista} />
                <ClickMapa activo={ubicando} onPunto={elegirLugar} />

                {sel.filter((id) => areas[id]).map((id) => (
                    <GeoJSON key={`area-${id}`} data={areas[id]} style={() => ({ color: "#1565c0", weight: 3, fillOpacity: 0.07 })} interactive={false} />
                ))}

                {resueltos.rows.map((r) => (
                    <CircleMarker
                        key={`res-${r.id_objeto}`}
                        center={[Number(r.gpslat), Number(r.gpslon)]}
                        radius={vista && vista.zoom >= 16 ? 14 : 11}
                        pathOptions={{ color: "#1b8a3a", weight: 3, fillColor: "#1b8a3a", fillOpacity: 0.18 }}
                        eventHandlers={{ click: () => { if (!ubicando) { setPunto(null); setResuelto(r); } } }}
                    />
                ))}

                {objetos.rows.map((o) => (
                    <CircleMarker
                        key={o.id_objeto}
                        center={[Number(o.gpslat), Number(o.gpslon)]}
                        radius={vista && vista.zoom >= 16 ? 8 : 5}
                        pathOptions={{ color: o.estado_gral ? colorEstado(o.estado_gral) : "#fff", weight: o.estado_gral ? 3 : 1, fillColor: colorTipo(tipoPorId[o.id_tipo]), fillOpacity: 0.92 }}
                        eventHandlers={{ click: () => { if (!ubicando) setPunto(o); } }}
                    />
                ))}

                {resuelto && (
                    <Popup key={`res-${resuelto.id_objeto}`} position={[Number(resuelto.gpslat), Number(resuelto.gpslon)]} eventHandlers={{ remove: () => setResuelto(null) }}>
                        <div className="popup">
                            <strong className="resuelto-titulo">✔ Reporte resuelto</strong>
                            <div>{resuelto.nombre || resuelto.codigo}</div>
                            <div className="popup-menor">{PROBLEMA_RESUELTO[resuelto.tipo] || "Tenía un problema"} · resuelto el {resuelto.fecha_txt}</div>
                        </div>
                    </Popup>
                )}

                {miPos && (<>
                    <Circle center={[miPos.lat, miPos.lon]} radius={Math.max(miPos.precision || 0, 5)} interactive={false}
                        pathOptions={{ color: "#1a73e8", weight: 1, fillColor: "#1a73e8", fillOpacity: 0.12 }} />
                    <CircleMarker center={[miPos.lat, miPos.lon]} radius={8} interactive={false}
                        pathOptions={{ color: "#fff", weight: 3, fillColor: "#1a73e8", fillOpacity: 1 }} />
                </>)}

                {reporte && reporte.lugar && (
                    <CircleMarker center={[reporte.lugar.lat, reporte.lugar.lon]} radius={12} interactive={false}
                        pathOptions={{ color: "#c62828", weight: 3, fillColor: "#c62828", fillOpacity: 0.25 }} />
                )}

                {punto && (
                    <Popup key={punto.id_objeto} position={[Number(punto.gpslat), Number(punto.gpslon)]} eventHandlers={{ remove: () => setPunto(null) }}>
                        <div className="popup">
                            <strong>{punto.nombre || punto.codigo}</strong>
                            <div className="popup-tipo">
                                <span className="punto-color" style={{ background: colorTipo(tipoPorId[punto.id_tipo]) }} />
                                {(tipoPorId[punto.id_tipo] || {}).nombre}
                            </div>
                            <div className="popup-tipo">
                                <span className="punto-color" style={{ background: colorEstado(punto.estado_gral) }} />
                                Estado: {nombreEstado(estadoPorCodigo[punto.estado_gral || ""] || { codigo: punto.estado_gral || "" })}
                            </div>
                            {punto.direcc && <div>{punto.direcc}</div>}
                            {punto.meses_rest != null && <div className="popup-menor">{textoRestante(punto.meses_rest)}</div>}
                            {(punto.ano_fab || punto.ano_inst || punto.meses_vid_util) && (
                                <div className="popup-menor">
                                    {punto.ano_fab ? `Fabricado ${punto.ano_fab}` : ""}
                                    {punto.ano_fab && punto.ano_inst ? " · " : ""}
                                    {punto.ano_inst ? `Instalado ${punto.ano_inst}` : ""}
                                    {punto.meses_vid_util ? `${punto.ano_fab || punto.ano_inst ? " · " : ""}Vida útil ${punto.meses_vid_util} meses (${(punto.meses_vid_util / 12).toFixed(1).replace(/\.0$/, "")} años)` : ""}
                                </div>
                            )}
                            <div className="popup-menor">Código {punto.codigo}</div>
                            <a href={`https://www.google.com/maps?q=${punto.gpslat},${punto.gpslon}`} target="_blank" rel="noopener noreferrer">Abrir en Google Maps</a>
                            <button type="button" className="popup-reportar" onClick={() => { setReporte({ objeto: punto }); setPunto(null); }}>⚠ Reportar un problema</button>
                        </div>
                    </Popup>
                )}
            </MapContainer>

            {/* Botón dentro del mapa */}
            <button type="button" className="btn-objetos" onClick={() => setPanel((p) => !p)} aria-expanded={panel} aria-controls="panel-objetos">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" />
                </svg>
                Objetos
                {sel.length > 0 && <span className="insignia">{sel.length}</span>}
            </button>

            <button type="button" className="btn-reportar" onClick={() => { setPanel(false); setPunto(null); setUbicando(true); }}>
                ⚠ Reportar algo que falta
            </button>
            {ubicando && (
                <div className="banner-ubicar" role="status">
                    <strong>Toca el mapa</strong> en el lugar que quieres reportar
                    <div className="banner-acciones">
                        <button type="button" className="atajo" onClick={usarMiUbicacion}>Usar mi ubicación</button>
                        <button type="button" className="atajo" onClick={() => setUbicando(false)}>Cancelar</button>
                    </div>
                </div>
            )}

            <button type="button" className={"btn-ubicacion" + (buscandoPos ? " buscando" : "")} onClick={irAMiUbicacion} disabled={buscandoPos}
                aria-label="Ir a mi ubicación" title="Ir a mi ubicación">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="3.2" /><circle cx="12" cy="12" r="7.5" /><line x1="12" y1="1.5" x2="12" y2="4" /><line x1="12" y1="20" x2="12" y2="22.5" /><line x1="1.5" y1="12" x2="4" y2="12" /><line x1="20" y1="12" x2="22.5" y2="12" />
                </svg>
            </button>

            {/* Columna arriba a la derecha: correo, instalar (si se puede) y ayuda */}
            <div className="barra-der">
                <a className="contacto" href={MAILTO} title={`Escríbenos a ${CORREO}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22 6 12 13 2 6" />
                    </svg>
                    <span>{CORREO}</span>
                </a>
                {puedeInstalar && (
                    <button type="button" className="btn-ayuda btn-instalar" onClick={() => { if (instalar) pedirInstalar(); else { setPanel(false); setAyuda("instalar"); } }} title="Instalar el mapa como app en este dispositivo" aria-label="Instalar en este dispositivo">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 3v12" /><polyline points="7 11 12 16 17 11" /><path d="M5 20h14" />
                        </svg>
                        <span className="btn-ayuda-texto">Instalar en este dispositivo</span>
                    </button>
                )}
                <button type="button" className="btn-ayuda" onClick={() => { setPanel(false); setAyuda(true); }} aria-label="Cómo funciona el mapa" title="Cómo funciona el mapa">
                    <span aria-hidden="true">?</span><span className="btn-ayuda-texto">¿Cómo funciona?</span>
                </button>
            </div>

            {!panel && sel.length === 0 && raices !== null && !errorRaices && (
                <div className="sugerencia">Pulsa <strong>Objetos</strong> para elegir una zona</div>
            )}
            {sel.length > 0 && (
                <div className="estado" role="status">
                    {cargandoObj ? "Cargando…" : `Mostrando ${fmt(objetos.rows.length)} de ${fmt(objetos.total)} objetos`}
                    {objetos.truncado && !cargandoObj && <span className="estado-alerta"> · acércate o filtra por tipo</span>}
                    {(rangosActivos || vidaFiltroActivo) && !cargandoObj && <span> · con filtros de año/vida útil</span>}
                </div>
            )}
            {aviso && (
                <div className="aviso" role="alert">
                    {aviso} <button type="button" onClick={() => setAviso(null)} aria-label="Cerrar aviso">×</button>
                </div>
            )}

            <aside id="panel-objetos" className={"panel" + (panel ? " abierto" : "")} aria-hidden={!panel} aria-label="Selección de objetos">
                <header className="panel-cab">
                    <h2>Objetos</h2>
                    <button type="button" className="icono" onClick={() => setPanel(false)} aria-label="Cerrar panel">×</button>
                </header>

                <div className="panel-cuerpo">
                    <section>
                        <h3><span className="paso">1</span>{tituloZonas}</h3>
                        {raices === null && !errorRaices && <p className="ayuda">Cargando…</p>}
                        {errorRaices && <p className="ayuda error">No se pudo cargar la lista: {errorRaices}</p>}
                        {raices !== null && raices.length === 0 && <p className="ayuda">Este sitio todavía no tiene objetos para mostrar.</p>}

                        {zonasElegidas.length > 0 && (
                            <div className="chips">
                                {zonasElegidas.map((r) => (
                                    <span key={r.id_objeto} className="chip">
                                        {r.nombre || r.codigo}
                                        <button type="button" onClick={() => alternarZona(r.id_objeto)} aria-label={`Quitar ${r.nombre || r.codigo}`}>×</button>
                                    </span>
                                ))}
                                {zonasElegidas.length > 1 && <button type="button" className="enlace" onClick={limpiarZonas}>quitar todas</button>}
                            </div>
                        )}

                        {raices !== null && raices.length > 0 && (<>
                            {raices.length > 8 && (
                                <input type="search" className="buscar" placeholder={`Buscar ${tituloZonas.toLowerCase()}…`} value={busca}
                                    onChange={(e) => setBusca(e.target.value)} aria-label={`Buscar ${tituloZonas.toLowerCase()}`} />
                            )}
                            <ul className="lista">
                                {zonasFiltradas.map((r) => (
                                    <li key={r.id_objeto}>
                                        <label>
                                            <input type="checkbox" checked={sel.includes(r.id_objeto)} onChange={() => alternarZona(r.id_objeto)} />
                                            {r.nombre || r.codigo}
                                        </label>
                                    </li>
                                ))}
                                {zonasFiltradas.length === 0 && <li className="ayuda">Sin resultados para «{busca}».</li>}
                            </ul>
                        </>)}
                    </section>

                    <section className={sel.length ? "" : "inactiva"}>
                        <h3>
                            <span className="paso">2</span>Tipos de objeto
                            {hayTipos && (<>
                                <button type="button" className="enlace" onClick={() => todosTipos(true)}>todos</button>
                                <button type="button" className="enlace" onClick={() => todosTipos(false)}>ninguno</button>
                            </>)}
                        </h3>
                        {!sel.length && <p className="ayuda">Elige primero una {tituloZonas.toLowerCase()}: aquí verás qué tipos de objeto hay dentro.</p>}
                        {sel.length > 0 && cargandoTipos && <p className="ayuda">Buscando tipos…</p>}
                        {sel.length > 0 && !cargandoTipos && !hayTipos && <p className="ayuda">No hay objetos con ubicación dentro de la selección.</p>}
                        <ul className="lista">
                            {tipos.map((t) => (
                                <li key={t.id_tipo}>
                                    <label>
                                        <input type="checkbox" checked={!tiposOff.has(t.id_tipo)} onChange={() => alternarTipo(t.id_tipo)} />
                                        <span className="punto-color" style={{ background: colorTipo(t) }} />
                                        {t.nombre}
                                        <span className="cantidad">{fmt(t.cantidad)}</span>
                                    </label>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section className={sel.length && hayTipos ? "" : "inactiva"}>
                        <h3>
                            <span className="paso">3</span>Estado
                            {estados.length > 1 && (<>
                                <button type="button" className="enlace" onClick={() => todosEstados(true)}>todos</button>
                                <button type="button" className="enlace" onClick={() => todosEstados(false)}>ninguno</button>
                            </>)}
                        </h3>
                        {!(sel.length && hayTipos) && <p className="ayuda">Elige zona y tipos: aquí podrás filtrar por el estado de los objetos (bueno, malo, quebrado...).</p>}
                        {sel.length > 0 && hayTipos && cargandoEstados && <p className="ayuda">Buscando estados…</p>}
                        {sel.length > 0 && hayTipos && !cargandoEstados && estados.length === 0 && tiposActivos.length > 0 && <p className="ayuda">Este sistema no registra estados.</p>}
                        <ul className="lista">
                            {estadosOrdenados.map((e) => (
                                <li key={e.codigo || "_sin"}>
                                    <label>
                                        <input type="checkbox" checked={!estadosOff.has(e.codigo)} onChange={() => alternarEstado(e.codigo)} />
                                        <span className="punto-color" style={{ background: colorEstado(e.codigo) }} />
                                        {e.codigo ? nombreEstado(e) : "Sin estado"}
                                        <span className="cantidad">{fmt(e.cantidad)}</span>
                                    </label>
                                </li>
                            ))}
                        </ul>
                        {estados.some((e) => e.codigo) && <p className="ayuda">El borde de cada punto indica su estado.</p>}
                    </section>

                    <section className={sel.length && hayTipos && rangosDatos.ano_fab && rangosDatos.ano_fab.min != null ? "" : "inactiva"}>
                        <h3>
                            <span className="paso">4</span>Año y vida útil
                            {rangosActivos && <button type="button" className="enlace" onClick={limpiarFiltros}>limpiar</button>}
                        </h3>
                        {!(sel.length && hayTipos && rangosDatos.ano_fab && rangosDatos.ano_fab.min != null) && (
                            <p className="ayuda">Elige zona y tipos: aquí podrás filtrar por año de fabricación, de instalación y vida útil.</p>
                        )}
                        {sel.length > 0 && hayTipos && rangosDatos.ano_fab && rangosDatos.ano_fab.min != null && (<>
                            {FILTROS_NUM.map(({ campo, titulo }) => {
                                const r = rangosDatos[campo] || {};
                                return (
                                    <fieldset key={campo} className="rango">
                                        <legend>{titulo}</legend>
                                        <label>Desde
                                            <input type="number" inputMode="numeric" min={r.min} max={r.max} placeholder={r.min != null ? String(r.min) : ""}
                                                value={filtros[campo][0]} onChange={(e) => cambiarFiltro(campo, 0, e.target.value)} />
                                        </label>
                                        <label>Hasta
                                            <input type="number" inputMode="numeric" min={r.min} max={r.max} placeholder={r.max != null ? String(r.max) : ""}
                                                value={filtros[campo][1]} onChange={(e) => cambiarFiltro(campo, 1, e.target.value)} />
                                        </label>
                                    </fieldset>
                                );
                            })}
                            <p className="ayuda">Al filtrar, los objetos sin ese dato quedan fuera.</p>
                        </>)}
                    </section>

                    <section className={sel.length && hayTipos && rangosDatos.meses_rest && rangosDatos.meses_rest.min != null ? "" : "inactiva"}>
                        <h3>
                            <span className="paso">5</span>Fin de vida útil
                            {vidaFiltroActivo && <button type="button" className="enlace" onClick={() => setVidaRango(["", ""])}>limpiar</button>}
                        </h3>
                        {!(sel.length && hayTipos && rangosDatos.meses_rest && rangosDatos.meses_rest.min != null) && (
                            <p className="ayuda">Elige zona y tipos: aquí podrás ver qué objetos ya cumplieron o están por cumplir su vida útil.</p>
                        )}
                        {sel.length > 0 && hayTipos && rangosDatos.meses_rest && rangosDatos.meses_rest.min != null && (<>
                            <div className="segmento" role="radiogroup" aria-label="Contar la vida útil desde">
                                <span>Contar desde:</span>
                                <label><input type="radio" name="vidaBase" checked={vidaBase === "inst"} onChange={() => setVidaBase("inst")} /> la instalación</label>
                                <label><input type="radio" name="vidaBase" checked={vidaBase === "fab"} onChange={() => setVidaBase("fab")} /> la fabricación</label>
                            </div>
                            <div className="atajos">
                                {PRESETS_VIDA.map((p) => (
                                    <button key={p.id} type="button" className={"atajo" + (presetVidaActivo(p) ? " activo" : "")} onClick={() => setVidaRango(p.rango)}>{p.texto}</button>
                                ))}
                            </div>
                            <fieldset className="rango">
                                <legend>Meses de vida útil restante</legend>
                                <label>Desde
                                    <input type="number" inputMode="numeric" placeholder={String(rangosDatos.meses_rest.min)} value={vidaRango[0]} onChange={(e) => cambiarVida(0, e.target.value)} />
                                </label>
                                <label>Hasta
                                    <input type="number" inputMode="numeric" placeholder={String(rangosDatos.meses_rest.max)} value={vidaRango[1]} onChange={(e) => cambiarVida(1, e.target.value)} />
                                </label>
                            </fieldset>
                            <p className="ayuda">
                                Restante = vida útil (meses) − 12 × ({new Date().getFullYear()} − año de {vidaBase === "inst" ? "instalación" : "fabricación"}).
                                Cero o negativo = ya cumplió su vida útil. Se calcula por año; si falta el año elegido se usa el otro. Sin datos, el objeto queda fuera al filtrar.
                            </p>
                        </>)}
                    </section>

                    <section className={sel.length ? "" : "inactiva"}>
                        <h3><span className="paso">6</span>Reportes resueltos</h3>
                        <ul className="lista">
                            <li>
                                <label>
                                    <input type="checkbox" checked={verResueltos} disabled={!sel.length} onChange={() => setVerResueltos((v) => !v)} />
                                    <span className="punto-color anillo-resuelto" />
                                    Mostrar los resueltos (últimos 12 meses)
                                    {verResueltos && sel.length > 0 && <span className="cantidad">{fmt(resueltos.total)}</span>}
                                </label>
                            </li>
                        </ul>
                        {!sel.length && <p className="ayuda">Elige una zona para ver qué reportes de los vecinos ya se resolvieron.</p>}
                        {verResueltos && sel.length > 0 && resueltos.total === 0 && <p className="ayuda">Todavía no hay reportes resueltos publicados en esta selección.</p>}
                        {verResueltos && resueltos.total > 0 && <p className="ayuda">Los círculos verdes marcan objetos con un reporte ya resuelto.</p>}
                    </section>

                    <p className="pie-panel"><button type="button" className="enlace-bt" onClick={() => setConsulta(true)}>Consultar un reporte</button> con tu código de seguimiento.</p>
                    <p className="pie-panel">¿Lo quieres con los datos de tu organización? <a href={MAILTO}>{CORREO}</a></p>
                </div>
            </aside>
            {reporte && <FormularioReporte contexto={reporte} tipos={tipos} onCerrar={() => setReporte(null)} />}
            {consulta && <ConsultaReporte onCerrar={() => setConsulta(false)} />}
            {ayuda && <Ayuda abrirInstalar={ayuda === "instalar"} leyenda={ESTADO_ORDEN.map((c) => ({ texto: ESTADO_NOMBRE[c], color: colorEstado(c) }))} correo={CORREO} mailto={MAILTO} onInstalar={instalar ? pedirInstalar : null} onCerrar={() => setAyuda(false)} />}
        </div>
    );
}
